import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { MODALS_NS } from '../../Modals/i18n';

interface ModalState {
  open: boolean;
  type: 'loading' | 'error' | 'confirmation';
  title?: string;
  message: string;
  onConfirm?: () => void;
  confirmText?: string;
}

export const useExportModal = (
  isLoading: boolean,
  data: any[],
  selectedBMT: string | number,
  onConfirm: () => void,
) => {
  const { t } = useTranslation(MODALS_NS);
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    type: 'loading',
    message: '',
  });

  useEffect(() => {
    if (modalState.open && modalState.type === 'loading' && !isLoading) {
      if (isEmpty(data)) {
        setModalState((prev) => ({
          ...prev,
          type: 'error',
          message: t('export.error.noData'),
        }));
      } else {
        setModalState((prev) => ({
          ...prev,
          type: 'confirmation',
          title: t('export.title', { bmt: selectedBMT }),
          message: t('export.message'),
          confirmText: t('export.confirmText'),
          onConfirm,
        }));
      }
    }
  }, [isLoading, data, selectedBMT, onConfirm, t, modalState.open, modalState.type]);

  const handleExport = useCallback(() => {
    if (isLoading) {
      setModalState({
        open: true,
        type: 'loading',
        message: t('export.loading.message'),
        title: t('export.loading.title'),
      });
      return;
    }

    if (isEmpty(data)) {
      setModalState({
        open: true,
        type: 'error',
        message: t('export.error.noData'),
      });
      return;
    }

    setModalState({
      open: true,
      type: 'confirmation',
      title: t('export.title', { bmt: selectedBMT }),
      message: t('export.message'),
      confirmText: t('export.confirmText'),
      onConfirm,
    });
  }, [isLoading, data, selectedBMT, onConfirm, t]);

  const handleCloseModal = () => {
    setModalState((prev) => ({ ...prev, open: false }));
  };

  return {
    modalState,
    handleExport,
    handleCloseModal,
  };
};
